/**
 * @module Model.Event
 *
 */
import { Model } from "typedefs";
import { StudentProfileModel } from "./scheduling/StudentProfileModel";
import { ScheduleResourceResponse } from "app/utils/request/scheduling/facilityResources";
import { Interval, DateTime } from "luxon";
import i18n from "app/i18n";

export enum EventType {
  LESSON_SCHEDULE = "lesson_schedule",
  COACH_SCHEDULE_BLOCK = "coach_schedule_block",
  PROGRAM = "program",
  TIME_SLOT_RESERVATION = "time_slot_reservation",
}

export enum RepeatType {
  DAILY = "daily",
  WEEKLY = "weekly",
  BIWEEKLY = "biweekly",
  MONTHLY = "monthly",
}

export type RepeatOption = {
  label: string;
  value: RepeatType | null;
};

// export const REPEAT_OPTIONS: RepeatOption[] = [
//   { label: i18n.t("Does not repeat"), value: null },
//   { label: i18n.t("Daily"), value: RepeatType.DAILY },
//   { label: i18n.t("Weekly"), value: RepeatType.WEEKLY },
//   { label: i18n.t("Bi-Weekly"), value: RepeatType.BIWEEKLY },
//   { label: i18n.t("Monthly"), value: RepeatType.MONTHLY },
// ];

export const getRepeatOptions = (): RepeatOption[] => [
  { label: i18n.t("Does not repeat"), value: null },
  { label: i18n.t("Daily"), value: RepeatType.DAILY },
  { label: i18n.t("Weekly"), value: RepeatType.WEEKLY },
  { label: i18n.t("Bi-Weekly"), value: RepeatType.BIWEEKLY },
  { label: i18n.t("Monthly"), value: RepeatType.MONTHLY },
];

// A calendar column for a bay carries this prefix. The prefix keeps a bay id
// apart from a coach id, because the facility calendar draws both kinds of
// column in the same grid.
export const BAY_COLUMN_PREFIX = "bay:";

export function bayColumnId(facilityResourceId: number): string {
  return `${BAY_COLUMN_PREFIX}${facilityResourceId}`;
}

export function isBayColumnId(columnId: string): boolean {
  return typeof columnId === "string" && columnId.startsWith(BAY_COLUMN_PREFIX);
}

// The id of the bay that a bay column stands for.
export function bayIdFromColumnId(columnId: string): number | null {
  if (!isBayColumnId(columnId)) {
    return null;
  }
  const id = Number(columnId.slice(BAY_COLUMN_PREFIX.length));
  return Number.isInteger(id) ? id : null;
}

/**
 * The bay a lesson holds, or null. A lesson can also hold a location from
 * older data. A location is not a bay, so it does not count.
 */
export function heldBayId(event: {
  scheduleResourceIds?: number[];
  scheduleResources?: ScheduleResourceResponse[];
}): number | null {
  const resources = event.scheduleResources ?? [];
  if (resources.length > 0) {
    return resources.find((resource) => resource.kind === "bookable")?.id ?? null;
  }
  return event.scheduleResourceIds?.[0] ?? null;
}

// The owner of a block that belongs to a bay. A block of a coach has the
// owner type "CoachProfile".
export const RESOURCE_BLOCK_OWNER_TYPE = "ScheduleResource";

/**
 * Tells if the event is a block of a bay (a resource block). A resource block
 * has no coach. It shows in the column of its bay only.
 */
export function isResourceBlock(event: {
  type?: EventType | string;
  ownerType?: string | null;
} | null | undefined): boolean {
  return (
    event?.type === EventType.COACH_SCHEDULE_BLOCK &&
    event?.ownerType === RESOURCE_BLOCK_OWNER_TYPE
  );
}

// The owner of a block in the block form: coaches or bays. Only the block
// form of the facility calendar sets it.
export type BlockOwnerMode = "coaches" | "resources";

export const BLOCK_FOR_COACHES: BlockOwnerMode = "coaches";
export const BLOCK_FOR_RESOURCES: BlockOwnerMode = "resources";

/**
 * The owner keys of a block save. A block for bays sends the bay ids, and the
 * service makes one block for each bay. A block for coaches sends the coach
 * ids and the private flag. A bay block is never private, so the payload
 * does not send the flag for it.
 *
 * The owner comes from the explicit choice "Applies to: Resources" only. A
 * lesson copy also carries the bay list of the lesson, so the presence of the
 * list does not make a block for bays.
 */
export function scheduleBlockOwnerPayload(data: {
  appliesTo?: BlockOwnerMode;
  scheduleResourceIds?: number[] | null;
  coachProfileIds?: string[];
  private?: boolean;
}): {
  scheduleResourceIds?: number[];
  coachProfileIds?: string[];
  private?: boolean;
} {
  if (data.appliesTo === BLOCK_FOR_RESOURCES) {
    return { scheduleResourceIds: data.scheduleResourceIds ?? [] };
  }

  return {
    private: data.private,
    coachProfileIds: data.coachProfileIds,
  };
}

export interface EventResponse extends Model {
  id: number;
  start: string;
  end: string;
  backgroundColor: string;
  type: EventType;
  studentProfiles?: any[];
  coachProfile?: any;
  timeZone: string;
  lessonRateId?: number;
  repeat: string | null;
  untilDate: string | null;
  ntes: string;
  private: boolean;
  facilityId: string;
  lessonRateTitle?: string;
  // CN-2711: the booking option this session came from hides its price.
  lessonRateHidePrice?: boolean;
  // CN-2808: note for facility personnel on a session. The API only returns it
  // to coaches, facility admins and schedulers, never to the athlete.
  privateNotes?: string | null;
  feedPostId: string;
  eventColor: string;
  payment_method: string;
  productType?: string;
  productName?: string;
  refundable?: boolean;
  scheduleResourceIds?: number[];
  scheduleResources?: ScheduleResourceResponse[];
}

export class EventModel implements Model {
  id: number;
  start: string;
  end: string;
  date: DateTime;
  backgroundColor: string;
  timeZone: string;
  interval: Interval;
  title: string;
  subTitle: string;
  type: EventType;
  location: string;
  notes: string;
  privateNotes: string | null;
  repeat: string | null;
  repeatLabel: string | null;
  untilDate: Date;
  recurrenceGroupId: number | null;
  durationInMinutes: number;
  resourceId: string;
  resourceIds: string[];
  scheduleResourceIds: number[];
  scheduleResources: ScheduleResourceResponse[];
  // The bay of a lesson in the event form: a bay id, or null for "Auto" (a
  // lesson with an option) and for "No resource" (a custom lesson).
  scheduleResourceId?: number | null;
  // The bay column of the click that opened a new event form. A lesson copy
  // has none, also when the lesson holds a bay.
  clickedBayId?: number | null;
  // The owner choice of the block form (facility calendar only).
  appliesTo?: BlockOwnerMode;

  coachProfile?: any;
  studentProfiles?: StudentProfileModel[];
  studentProfileIds?: number[];
  lessonRateId?: number;
  rate?: number;
  rateCents?: number;
  rateCurrency?: string;
  private?: boolean;

  startTimeString: string;
  endTimeString: string;
  intervalTimeString: string;
  timeZoneShortString: string;
  eventColor: string;
  scheduleProviderDisplayName: string;
  externalCalendarId: string;

  spotsBooked?: number;
  capacity?: number;
  registrationStatusCode?: string;
  registrationStatus?: string;
  lessonRateTitle?: string;
  lessonRateHidePrice?: boolean;
  paying_athlete_id?: string;
  payer_email?: string;
  payment_method?: string;
  online_payment_type?: string;
  payment_status?: string;
  selected_package_id?: string;
  selectedPackage?: {
    stripe_product_rate_link_id: string;
    stripe_product_id: string;
    student_profile_id: number;
    remaining_sessions: number,
  };
  productName?: string;
  productType?: string;
  refundable?: boolean;
  charge_timing?: string;
  payment_editable?: boolean;
  preservePaymentDefaults?: boolean;
  payment_cancellable?: boolean;

  constructor(event: any) {
    this.id = event.id;
    this.start = event.start;
    this.end = event.end;
    this.backgroundColor = event.backgroundColor;
    this.timeZone = event.timeZone;
    this.title = event.title;
    this.untilDate = new Date(event.untilDate);
    this.recurrenceGroupId = event.recurrenceGroupId;
    this.resourceId = event.coachProfileId;

    // A booking also belongs in the column of the bay it holds. The calendar
    // merges `resourceId` and `resourceIds`, and it drops an id that matches
    // no column of the current view.
    this.scheduleResourceIds = event.scheduleResourceIds ?? [];
    this.scheduleResources = event.scheduleResources ?? [];
    this.resourceIds = this.scheduleResourceIds.map((id) => bayColumnId(id));

    this.interval = Interval.fromDateTimes(
      DateTime.fromISO(this.start).setZone(this.timeZone),
      DateTime.fromISO(this.end).setZone(this.timeZone),
    );
    this.durationInMinutes = this.interval.toDuration("minutes").minutes;

    this.date = this.interval.start;

    this.startTimeString = this.interval.start.toLocaleString(
      DateTime.TIME_SIMPLE,
    );
    this.endTimeString = this.interval.end.toLocaleString(DateTime.TIME_SIMPLE);
    this.timeZoneShortString = this.interval.start.toFormat("ZZZZ");

    this.intervalTimeString = `${this.startTimeString} - ${this.endTimeString} ${this.timeZoneShortString}`;
    this.notes = event.notes;
    this.privateNotes = event.privateNotes ?? null;
    this.externalCalendarId = event.externalCalendarId;

    this.repeat = event.repeat;
    this.repeatLabel = null;
    if (this.repeat) {
      this.repeatLabel =
        getRepeatOptions().find(
          (ropt: RepeatOption): boolean => ropt.value === this.repeat,
        )?.label || null;
    }

    this.lessonRateTitle = event.lessonRateTitle;
    this.lessonRateHidePrice = event.lessonRateHidePrice;
    this.eventColor = event.color;
    this.productName = event.productName;
    this.productType = event.productType;
  }

  public static apiPayload(
    data: EventModel & { coachProfileIds: string[], stripeProductRateLinkId: number, notifyStudents?: boolean },
    operation: string,
  ) {
    let payload: any = {
      start: data.start,
      end: data.end,
      notes: data.notes,
      type: data.type,
    };

    if (data.type === "lesson_schedule") {
      // Every lesson save from the form names the bay, also on edit. The
      // service reads a request with the key as a bay choice: a bay id, or
      // null for "Auto" or "No resource". The key is not in the payment block
      // below, because a lesson with a fixed payment can still move to
      // another bay. A series edit ("this and following") removes the key
      // again when the user did not change the bay (see bayKeyForScope).
      payload = {
        ...payload,
        studentProfileIds: data.studentProfileIds,
        location: data.location,
        scheduleResourceId: data.scheduleResourceId ?? null,
        // CN-2798: always a real boolean; the backend treats a missing value as true.
        notifyStudents: data.notifyStudents !== false,
        privateNotes: data.privateNotes,
      };

      if (data.payment_editable !== false) {
        payload = {
          ...payload,
          lessonRateId: data.lessonRateId,
          rate: data.rate,
          rateCents: data.rateCents,
          rateCurrency: data.rateCurrency,
          paying_athlete_id: data.paying_athlete_id,
          payment_method: data.payment_method,
          online_payment_type: data.online_payment_type,
          charge_timing: data.charge_timing,
          payment_status: data.payment_status,
          stripeProductRateLinkId: data.stripeProductRateLinkId,
        };
      }

      if (data.coachProfileIds && data.coachProfileIds.length > 0) {
        payload = {
          ...payload,
          coachProfileId: data.coachProfileIds[0],
        };
      }
    } else {
      payload = {
        ...payload,
        title: data.title,
        ...scheduleBlockOwnerPayload(data),
      };
    }

    if (operation === "create") {
      if (data.repeat) {
        const untilDate = DateTime.fromJSDate(data.untilDate).toISODate();
        payload = {
          ...payload,
          repeat: data.repeat,
          untilDate,
        };
      }
    } else {
      payload = {
        ...payload,
        id: data.id,
        recurrenceGroupId: data.recurrenceGroupId,
      };
    }

    return payload;
  }

  public static buildInstance(event: EventResponse) {
    switch (event.type) {
      case "lesson_schedule":
        return new LessonSchedule(event);
      case "coach_schedule_block":
        return new CoachScheduleBlock(event);
      case "program":
        return new Program(event);
      case "time_slot_reservation":
        return new TimeSlotReservation(event);
      default:
        throw new Error("Invalid event type");
    }
  }
}

export class LessonSchedule extends EventModel {
  studentProfiles: StudentProfileModel[];
  coachProfile: any;
  lessonRateId: number;
  location: string;
  rate: number;
  rateCents: number;
  rateCurrency: string;
  facilityId: string;
  scheduleProviderDisplayName: string;
  feedPostId: string;
  refundable?: boolean;
  invoiceUrl?: string | null;
  sharePaymentUrl?: string | null;
  // Set by the backend only for staff, when payment_status is failed.
  failure_reason?: string | null;
  failure_message?: string | null;

  constructor(event: any) {
    super(event);
    this.lessonRateId = event.lessonRateId;
    // The event form starts from the bay the lesson holds.
    this.scheduleResourceId = heldBayId(this);

    if (event?.studentProfiles === undefined) { return; }
    this.studentProfiles = event.studentProfiles.map(
      (studentProfile: any): StudentProfileModel => {
        return new StudentProfileModel(studentProfile);
      },
    );

    this.coachProfile = event.coachProfile;

    if (this.studentProfiles.length === 1) {
      this.title = this.studentProfiles[0].fullName;
    } else {
      this.title = `${this.studentProfiles.length} students`;
    }

    this.payment_method = event.paymentMethod;
    this.payment_status = event.paymentStatus;
    this.failure_reason = event.failureReason;
    this.failure_message = event.failureMessage;
    this.paying_athlete_id = event.payingAthleteId;
    this.payer_email = event.payerEmail;
    this.online_payment_type = event.onlinePaymentType;
    this.charge_timing = event.chargeTiming;
    this.selected_package_id = event.selectedPackageId;
    this.location = event.location;
    this.rate = event.rate;
    this.rateCents = event.rateCents;
    this.rateCurrency = event.rateCurrency;
    this.refundable = event.refundable;
    this.payment_editable = event.paymentEditable;
    this.payment_cancellable = event.paymentCancellable;
    this.invoiceUrl = event.invoiceUrl;
    this.sharePaymentUrl = event.sharePaymentUrl;
    this.type = EventType.LESSON_SCHEDULE;
    this.backgroundColor = "#fff";
    this.facilityId = event.facilityId;
    this.scheduleProviderDisplayName = event.scheduleProviderDisplayName;
    this.feedPostId = event.feedPostId ? event.feedPostId : null;
  }
}

export class CoachScheduleBlock extends EventModel {
  private: boolean;
  scheduleProviderDisplayName: string;
  feedPostId: string;
  // "CoachProfile" for a block of a coach, "ScheduleResource" for a block of
  // a bay. A bay block has no coach.
  ownerType?: string;
  ownerId?: number | string;

  constructor(event: any) {
    super(event);

    this.subTitle = "";
    this.type = EventType.COACH_SCHEDULE_BLOCK;
    this.backgroundColor = "#f4f4f4";
    this.title = event.title || "Unavailable";
    this.private = event.private;
    this.scheduleProviderDisplayName = event.scheduleProviderDisplayName;
    this.coachProfile = event.coachProfile;
    this.feedPostId = event.feedPostId ? event.feedPostId : null;
    this.ownerType = event.ownerType;
    this.ownerId = event.ownerId;
  }
}

export class Program extends EventModel {
  private: boolean;
  feedPostId: string;

  constructor(event: any) {
    super(event);
    this.type = EventType.PROGRAM;
    this.backgroundColor = "#fff";
    this.title = event.title;
    this.subTitle = event.subtitle;
    this.spotsBooked = event.spotsBooked;
    this.capacity = event.capacity;
    this.registrationStatusCode = event.registrationStatusCode;
    this.private = event.private;
    this.registrationStatus = event.registrationStatus;
    this.resourceIds = event.coachProfileIds;
    this.feedPostId = event.feedPostId ? event.feedPostId : null;
  }
}

export class TimeSlotReservation extends EventModel {
  expiresAt: DateTime;

  constructor(event: any) {
    super(event);
    this.type = EventType.TIME_SLOT_RESERVATION;
    this.backgroundColor = "#FFA00033";
    this.studentProfiles = [new StudentProfileModel(event.studentProfiles[0])];
    this.title = this.studentProfiles[0].fullName;
    this.expiresAt = DateTime.fromISO(event.expiresAt).setZone(this.timeZone);
    this.payment_method = event.paymentMethod;
    this.coachProfile = event.coachProfile;
  }
}
